import { PermissionContext } from '../rpe/rpe-permissions.model';
import { USER_PROFILE_ALL_FACILITIES_ID } from './constants';

export interface UserProfileFacilitySource {
  defaultLocation?: { id?: unknown } | null;
  otherLocation?: Array<{ id?: unknown }> | null;
  isAllWarehouse?: boolean;
  isSuperAdmin?: boolean;
}

/** My Profile "All" default facility — only when RPE on and user is not super admin. */
export function isAllFacilitiesProfileOptionAvailable(
  profile: UserProfileFacilitySource | null | undefined,
  options: { enforcementEnabled: boolean; isSuperAdmin?: boolean }
): boolean {
  if (!options.enforcementEnabled) {
    return false;
  }
  if (options.isSuperAdmin === true || profile?.isSuperAdmin === true) {
    return false;
  }
  return true;
}

/** Profile default facility is All (sentinel 0 or isAllWarehouse flag). */
export function isAllFacilitiesDefaultLocation(
  locationId: unknown,
  profile?: UserProfileFacilitySource | null
): boolean {
  if (profile?.isAllWarehouse === true) {
    return true;
  }
  return Number(locationId) === USER_PROFILE_ALL_FACILITIES_ID;
}

/** Real warehouse ids assigned on the user profile (excludes sentinel 0). */
export function getAssignedFacilityIds(
  profile: UserProfileFacilitySource | null | undefined
): number[] {
  if (!profile) {
    return [];
  }

  const ids = new Set<number>();
  const defaultId = Number(profile.defaultLocation?.id);
  if (Number.isFinite(defaultId) && defaultId > 0) {
    ids.add(defaultId);
  }

  for (const location of profile.otherLocation ?? []) {
    const id = Number(location?.id);
    if (Number.isFinite(id) && id > 0) {
      ids.add(id);
    }
  }

  return Array.from(ids).sort((a, b) => a - b);
}

/** Real warehouse ids from loaded effective-permissions contexts. */
export function getPermissionContextLocationIds(
  permissionContexts: PermissionContext[]
): number[] {
  return [
    ...new Set(
      permissionContexts
        .map((ctx) => Number(ctx.locationId))
        .filter((id) => Number.isFinite(id) && id > 0)
    ),
  ].sort((a, b) => a - b);
}

/**
 * Location ids to union when Default Facility = All.
 * Prefer permission contexts (authoritative for RPE); fall back to profile assignments.
 */
export function getAllFacilitiesMergeLocationIds(
  profile: UserProfileFacilitySource | null | undefined,
  permissionContexts: PermissionContext[]
): number[] {
  const contextIds = getPermissionContextLocationIds(permissionContexts);
  const profileIds = getAssignedFacilityIds(profile);

  if (contextIds.length >= 2) {
    if (profileIds.length >= 2) {
      const profileIdSet = new Set(profileIds);
      const intersected = contextIds.filter((id) => profileIdSet.has(id));
      return (intersected.length >= 2 ? intersected : contextIds).sort(
        (a, b) => a - b
      );
    }
    return contextIds;
  }

  return profileIds;
}

export function isMultiWarehouseAllFacilitiesMode(
  profile: UserProfileFacilitySource | null | undefined,
  profileDefaultLocationId: number | null | undefined,
  permissionContexts: PermissionContext[]
): boolean {
  if (!isAllFacilitiesDefaultLocation(profileDefaultLocationId, profile)) {
    return false;
  }

  return getAllFacilitiesMergeLocationIds(profile, permissionContexts).length >= 2;
}

/** True when Default Facility = All but user only has one warehouse — same access as picking that warehouse. */
export function isSingleWarehouseAllFacilitiesEquivalent(
  profile: UserProfileFacilitySource | null | undefined,
  permissionContexts: PermissionContext[] = []
): boolean {
  const profileDefaultLocationId = profile?.isAllWarehouse
    ? USER_PROFILE_ALL_FACILITIES_ID
    : Number(profile?.defaultLocation?.id);

  if (
    !isAllFacilitiesDefaultLocation(profileDefaultLocationId, profile)
  ) {
    return false;
  }

  return (
    getAllFacilitiesMergeLocationIds(profile, permissionContexts).length < 2
  );
}

/** Resolved NGXS / RPE active location — single-warehouse + All → that warehouse id, not 0. */
export function resolveProfileActiveLocationId(
  profile: UserProfileFacilitySource | null | undefined,
  permissionContexts: PermissionContext[] = []
): number | null {
  if (!profile) {
    return null;
  }

  const rawDefaultLocationId = profile.isAllWarehouse
    ? USER_PROFILE_ALL_FACILITIES_ID
    : Number(profile.defaultLocation?.id);

  if (!Number.isFinite(rawDefaultLocationId)) {
    return null;
  }

  return resolveEffectivePermissionLocationId(
    profile,
    rawDefaultLocationId,
    permissionContexts
  );
}

/**
 * Maps profile default to the permission context key.
 * Single-warehouse + All → that warehouse id; multi + All → 0; else raw default.
 */
export function resolveEffectivePermissionLocationId(
  profile: UserProfileFacilitySource | null | undefined,
  profileDefaultLocationId: number | null | undefined,
  permissionContexts: PermissionContext[] = []
): number | null {
  if (profileDefaultLocationId == null || Number.isNaN(profileDefaultLocationId)) {
    return null;
  }

  if (!isAllFacilitiesDefaultLocation(profileDefaultLocationId, profile)) {
    return profileDefaultLocationId;
  }

  const mergeIds = getAllFacilitiesMergeLocationIds(profile, permissionContexts);
  if (mergeIds.length >= 2) {
    return USER_PROFILE_ALL_FACILITIES_ID;
  }

  if (mergeIds.length === 1) {
    return mergeIds[0];
  }

  return profileDefaultLocationId;
}

export function isAllFacilitiesViewOnlyMode(
  profile: UserProfileFacilitySource | null | undefined,
  profileDefaultLocationId: number | null | undefined,
  permissionContexts: PermissionContext[] = [],
  options?: { enforcementEnabled?: boolean; isSuperAdmin?: boolean }
): boolean {
  if (options?.enforcementEnabled === false || options?.isSuperAdmin === true) {
    return false;
  }

  return isMultiWarehouseAllFacilitiesMode(
    profile,
    profileDefaultLocationId,
    permissionContexts
  );
}

function modulePermissionKey(module: {
  moduleName: string;
  pageId?: number;
}): string {
  return `${module.moduleName}::${module.pageId ?? 0}`;
}

/** Union modules/actions/fields across assigned warehouses for Default Facility = All. */
export function mergePermissionContexts(
  contexts: PermissionContext[],
  assignedLocationIds: number[]
): PermissionContext | undefined {
  const assignedIdSet = new Set(assignedLocationIds);
  const relevant = contexts.filter((ctx) => assignedIdSet.has(ctx.locationId));

  if (!relevant.length) {
    return undefined;
  }

  if (relevant.length === 1) {
    return relevant[0];
  }

  const moduleMap = new Map<string, PermissionContext['modules'][number]>();

  for (const context of relevant) {
    for (const module of context.modules) {
      const key = modulePermissionKey(module);
      const existing = moduleMap.get(key);

      if (!existing) {
        moduleMap.set(key, {
          ...module,
          actionPermissions: [...module.actionPermissions],
          fieldPermissions: [...module.fieldPermissions],
        });
        continue;
      }

      const actionIds = new Set(
        existing.actionPermissions.map((action) => action.actionId)
      );
      for (const action of module.actionPermissions) {
        if (!actionIds.has(action.actionId)) {
          existing.actionPermissions.push(action);
          actionIds.add(action.actionId);
        }
      }

      const fieldIds = new Set(
        existing.fieldPermissions.map((field) => field.fieldId)
      );
      for (const field of module.fieldPermissions) {
        if (!fieldIds.has(field.fieldId)) {
          existing.fieldPermissions.push(field);
          fieldIds.add(field.fieldId);
        }
      }
    }
  }

  return {
    locationId: USER_PROFILE_ALL_FACILITIES_ID,
    modules: Array.from(moduleMap.values()),
  };
}

export function isViewOnlyRpeActionName(actionName: string): boolean {
  const normalized = actionName.trim().toLowerCase();
  return normalized === 'view' || normalized === 'bulk view';
}
